import {
  Building2,
  Globe,
  Loader2,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { enhancedApi as firmsApi } from '@/services/api/generated/firmsService';
import {
  type SmsTemplateCategory,
  type SmsTemplateListResponse,
  type SmsTemplateSchema,
  useGlobalSmsTemplatesListQuery,
} from '@/services/api/generated/smsTemplatesService';

import { SmsTemplateEditor } from './SmsTemplateEditor';
import { SmsTemplateList } from './SmsTemplateList';

const CATEGORY_OPTIONS: {
  value: SmsTemplateCategory | 'all';
  label: string;
}[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'Document Collection', label: 'Document Collection' },
  {
    value: 'Initial Treatment Assessment',
    label: 'Initial Treatment Assessment',
  },
  { value: 'Ongoing Treatment Check-In', label: 'Ongoing Treatment Check-In' },
];

type ViewScope = 'global' | 'firm';

export const SmsTemplateAdminTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<
    SmsTemplateCategory | 'all'
  >('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<SmsTemplateSchema | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<SmsTemplateSchema | null>(null);
  const [viewScope, setViewScope] = useState<ViewScope>('global');
  const [selectedFirmId, setSelectedFirmId] = useState<string | null>(null);

  // Fetch firms list for filtering
  const { data: firmsList } = firmsApi.useFirmsListQuery();

  // Global templates query
  const {
    data: globalTemplatesData,
    isLoading: isLoadingGlobal,
    isFetching: isFetchingGlobal,
    refetch: refetchGlobal,
  } = useGlobalSmsTemplatesListQuery({
    activeOnly: false,
  });

  // Firm-specific templates query
  const {
    data: firmTemplatesData,
    isLoading: isLoadingFirm,
    isFetching: isFetchingFirm,
    refetch: refetchFirm,
  } = firmsApi.useFirmSmsTemplatesListQuery(
    { firmId: selectedFirmId ?? '', activeOnly: false },
    { skip: !selectedFirmId || viewScope !== 'firm' },
  );

  const isLoading = viewScope === 'global' ? isLoadingGlobal : isLoadingFirm;
  const isFetching = viewScope === 'global' ? isFetchingGlobal : isFetchingFirm;

  // Workaround: API returns single object but types say array
  const globalTemplates = useMemo(() => {
    if (!globalTemplatesData) return [];
    const data = globalTemplatesData as unknown;
    if (Array.isArray(data)) {
      return (data as SmsTemplateListResponse[]).flatMap(
        (r) => r.templates ?? [],
      );
    }
    return (data as SmsTemplateListResponse).templates ?? [];
  }, [globalTemplatesData]);

  const firmTemplates = useMemo(() => {
    if (!firmTemplatesData) return [];
    const data = firmTemplatesData as unknown;
    let allTemplates: SmsTemplateSchema[] = [];
    if (Array.isArray(data)) {
      allTemplates = (data as SmsTemplateListResponse[]).flatMap(
        (r) => r.templates ?? [],
      );
    } else {
      allTemplates = (data as SmsTemplateListResponse).templates ?? [];
    }
    // Filter out global templates - only show firm-specific ones
    return allTemplates.filter((t) => !t.is_global);
  }, [firmTemplatesData]);

  const templates = viewScope === 'global' ? globalTemplates : firmTemplates;

  const handleCreateNew = useCallback(() => {
    setEditingTemplate(null);
    setEditorOpen(true);
  }, []);

  const handleEdit = useCallback((template: SmsTemplateSchema) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  }, []);

  const handleDeleteClick = useCallback((template: SmsTemplateSchema) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!templateToDelete) return;

    // DELETE endpoint not available in backend API yet
    // Backend needs: DELETE /api/sms-templates/global/{id}/
    toast.error(
      'Delete not available - backend endpoint missing. Mark template as inactive instead.',
    );
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  }, [templateToDelete]);

  const handleEditorClose = useCallback(() => {
    setEditorOpen(false);
    setEditingTemplate(null);
  }, []);

  const handleSaved = useCallback(() => {
    refetchGlobal();
    if (selectedFirmId) {
      refetchFirm();
    }
  }, [refetchGlobal, refetchFirm, selectedFirmId]);

  const handleRefresh = useCallback(() => {
    if (viewScope === 'global') {
      refetchGlobal();
    } else if (selectedFirmId) {
      refetchFirm();
    }
  }, [viewScope, selectedFirmId, refetchGlobal, refetchFirm]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">
              SMS Templates
            </h2>
            <p className="text-sm text-zinc-500">
              Manage message templates for treatment communications
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              className="gap-1.5"
            >
              <RefreshCw
                className={cn('h-4 w-4', isFetching && 'animate-spin')}
              />
              Refresh
            </Button>
            <Button size="sm" onClick={handleCreateNew} className="gap-1.5">
              <Plus className="h-4 w-4" />
              New Template
            </Button>
          </div>
        </div>

        {/* View Scope Tabs */}
        <div className="mb-4">
          <Tabs
            value={viewScope}
            onValueChange={(v) => setViewScope(v as ViewScope)}
          >
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="global" className="gap-2">
                <Globe className="h-4 w-4" />
                Global Templates
              </TabsTrigger>
              <TabsTrigger value="firm" className="gap-2">
                <Building2 className="h-4 w-4" />
                Firm Templates
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Firm Selector (only for firm view) */}
        {viewScope === 'firm' && (
          <div className="mb-4">
            <Select
              value={selectedFirmId ?? undefined}
              onValueChange={(v) => setSelectedFirmId(v)}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a firm to view its templates..." />
              </SelectTrigger>
              <SelectContent>
                {firmsList?.map((firm) => (
                  <SelectItem key={firm.id} value={firm.id}>
                    {firm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={categoryFilter}
            onValueChange={(v) =>
              setCategoryFilter(v as SmsTemplateCategory | 'all')
            }
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {viewScope === 'firm' && !selectedFirmId ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Building2 className="h-12 w-12 text-zinc-300 mb-4" />
            <p className="text-zinc-500">Select a firm to view its templates</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          </div>
        ) : (
          <SmsTemplateList
            templates={templates}
            searchQuery={searchQuery}
            categoryFilter={categoryFilter === 'all' ? null : categoryFilter}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            canEdit
          />
        )}
      </div>

      {/* Template Editor Modal */}
      <SmsTemplateEditor
        open={editorOpen}
        onOpenChange={handleEditorClose}
        template={editingTemplate}
        onSaved={handleSaved}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{templateToDelete?.name}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
